import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { CheckCircle2Icon, Edit3Icon, FileTextIcon, Trash2Icon } from '@/shared/ui/icons';
import { PageHeader } from '@/shared/ui/layout/PageHeader';
import { Section } from '@/shared/ui/layout/Section';
import { ClientStatusChip } from '@/shared/ui/chips/ClientStatusChip';
import { PartyStatusChip } from '@/shared/ui/chips/PartyStatusChip';
import { Spinner } from '@/shared/ui/feedback/Spinner';
import { ErrorState } from '@/shared/ui/feedback/ErrorState';
import { Button } from '@/shared/ui/buttons/Button';
import { ConfirmDialog } from '@/shared/ui/dialogs/ConfirmDialog';
import { useMutate } from '@/shared/hooks/useMutate';
import { useToast } from '@/shared/hooks/useToast';
import { queryKeys } from '@/shared/api/queryKeys';
import { extractErrorMessage } from '@/shared/api/errors';
import { apiParcelsConfirmShippingPost, apiParcelsDelete, useParcelDetail } from '@/entities/parcels';
import { useParcelProducts } from '@/entities/parcels-products';
import { useParcelCharges } from '@/entities/charges';
import { usePinflLimit } from '@/entities/pinfl';
import { useSplitsByParcel } from '@/entities/splits';
import {
  FeaturesSplitsStatusChip,
  FeaturesSplitsTargetCard,
} from '@/features/splits';
import { formatUsd } from '@/shared/utils/currency';
import { formatDateTime } from '@/shared/utils/date';
import { cn } from '@/shared/utils/cn';

const Row = ({ label, value }: { label: string; value: ReactNode }) => (
  <div className="flex items-start justify-between gap-3 py-1.5 text-sm">
    <span className="text-ink-muted">{label}</span>
    <span className="text-right font-medium text-ink">{value || '—'}</span>
  </div>
);

// Full-width recipients section for split parcels. Owns its own data hook so
// the parent's hook count stays stable across the non-split early returns.
const SplitRecipientsSection = ({ parcelId }: { parcelId: string }) => {
  const { t } = useTranslation();
  const splitsQuery = useSplitsByParcel(parcelId);
  const splits = splitsQuery.data ?? [];
  // Prefer the split that actually has targets; fall back to the first.
  const activeSplit = splits.find((s) => s.targets.length > 0) ?? splits[0];
  const targets = activeSplit?.targets ?? [];

  return (
    <Section title={t('parcelDetail:sections.recipients')}>
      {splitsQuery.isLoading ? (
        <div className="flex justify-center py-4">
          <Spinner />
        </div>
      ) : targets.length === 0 ? (
        <p className="text-sm text-ink-muted">{t('splits:targetsEmpty')}</p>
      ) : (
        <div className="flex flex-col gap-3">
          {targets.map((target) => (
            <FeaturesSplitsTargetCard key={target.id} target={target} />
          ))}
        </div>
      )}
    </Section>
  );
};

// Self-contained charges/payments section. Owns its own data hook so the
// parent's hook count stays stable across the early returns; renders nothing
// when the parcel has no charges.
const ParcelChargesSection = ({ parcelId }: { parcelId: string }) => {
  const { t } = useTranslation();
  const chargesQuery = useParcelCharges(parcelId);
  const charges = chargesQuery.data ?? [];

  if (chargesQuery.isLoading) {
    return (
      <Section title={t('parcelDetail:charges.title')}>
        <div className="flex justify-center py-4">
          <Spinner />
        </div>
      </Section>
    );
  }

  if (!chargesQuery.isLoading && charges.length === 0) return null;

  const unpaidTotal = charges.filter((c) => !c.is_paid).reduce((s, c) => s + Number(c.amount), 0);

  return (
    <Section title={t('parcelDetail:charges.title')}>
      {charges.map((c) => (
        <div key={c.id} className="flex items-center justify-between gap-3 py-1.5 text-sm">
          <span className="text-ink-muted">{t(`parcelDetail:charges.types.${c.type}`)}</span>
          <span className="flex items-center gap-2">
            <span className="font-medium text-ink">{formatUsd(Number(c.amount))}</span>
            {Number(c.amount) === 0 ? (
              <span className="rounded-full border border-emerald-200 bg-emerald-50 px-2.5 py-0.5 text-xs font-semibold text-emerald-700">
                {t('parcelDetail:charges.free')}
              </span>
            ) : c.is_paid ? (
              <span className="rounded-full border border-emerald-200 bg-emerald-50 px-2.5 py-0.5 text-xs font-semibold text-emerald-700">
                {t('parcelDetail:charges.paid')}
              </span>
            ) : (
              <span className="rounded-full border border-amber-200 bg-amber-50 px-2.5 py-0.5 text-xs font-semibold text-amber-700">
                {t('parcelDetail:charges.unpaid')}
              </span>
            )}
          </span>
        </div>
      ))}
      <div className="flex items-center justify-between border-t border-slate-100 pt-2 text-sm">
        <span className="text-ink-muted">{t('parcelDetail:charges.totalPayable')}</span>
        <span className="text-base font-semibold text-ink">{formatUsd(unpaidTotal)}</span>
      </div>
    </Section>
  );
};

export const WidgetsParcelDetailMain = () => {
  const { t } = useTranslation();
  const { id = '' } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const toast = useToast();
  const parcelQuery = useParcelDetail(id);
  const productsQuery = useParcelProducts(id);
  // Recipient PINFL customs limit. Called unconditionally (before the early
  // returns) with the parcel's PINFL when available; the hook self-disables
  // until it has a complete PINFL, so the empty-string fallback is harmless.
  const limitQuery = usePinflLimit(parcelQuery.data?.pinfl ?? '');
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [confirmShip, setConfirmShip] = useState(false);

  const remove = useMutate({
    api: () => apiParcelsDelete(id),
    invalidateKeys: [queryKeys.parcels.all, queryKeys.pinfl.all],
    onSuccess: () => {
      toast.success(t('parcels:toast.deleted'));
      navigate('/parcels');
    },
    onError: (_e, message) => toast.error(message),
  });

  const confirmShip$ = useMutate({
    api: () => apiParcelsConfirmShippingPost({ parcel_ids: [id] }),
    invalidateKeys: [queryKeys.parcels.all, queryKeys.parcels.detail(id)],
    onSuccess: (res) => {
      if (res.confirmed > 0) {
        toast.success(t('parcels:confirmShipping.toast.success', { count: res.confirmed }));
      }
      if (res.skipped.length > 0) {
        toast.info(t('parcels:confirmShipping.toast.skipped', { count: res.skipped.length }));
      }
      setConfirmShip(false);
    },
    onError: (_e, message) => {
      toast.error(message);
      setConfirmShip(false);
    },
  });

  if (parcelQuery.isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Spinner size="lg" />
      </div>
    );
  }
  if (parcelQuery.error || !parcelQuery.data) {
    return (
      <div className="p-4">
        <ErrorState
          message={extractErrorMessage(parcelQuery.error, t('parcelDetail:notFound'))}
          onRetry={() => void parcelQuery.refetch()}
        />
      </div>
    );
  }

  const parcel = parcelQuery.data;
  const products = productsQuery.data ?? parcel.products ?? [];
  // Canonical client-facing status. Backend persists the workflow on
  // ``Parcel.fulfillment_status`` and exposes it under ``client_status``; the
  // ``IN_PROGRESS`` fallback covers older payloads from pre-workflow builds.
  const clientStatus = parcel.client_status ?? 'IN_PROGRESS';
  // 2026-05-30 owner rule: once the user presses Confirm shipping (status
  // leaves IN_PROGRESS) the parcel is frozen on the client side — no edit,
  // no delete, no declaration. Admin keeps these capabilities.
  const isInProgress = clientStatus === 'IN_PROGRESS';
  const canDeclare = isInProgress;
  const editable = isInProgress;
  const deletable = isInProgress;
  // 2026-05-30 owner: confirm-shipping for a single parcel can also be triggered
  // from its detail page. Same eligibility as the bulk action on /parcels:
  // admin-approved, still IN_PROGRESS, at least one product declared. 2026-05-31
  // owner addendum: the physical box must already be inside the JP warehouse
  // (``is_in_warehouse=true``) before the button is offered — otherwise we
  // surface a short explanatory line so the user knows why it's hidden.
  const isInWarehouse = parcel.is_in_warehouse === true;
  const canConfirmShip =
    isInProgress &&
    parcel.status === 'APPROVED' &&
    products.length > 0 &&
    isInWarehouse;
  // Show the "waiting for warehouse" hint only when the only thing blocking
  // confirm-shipping is the missing warehouse arrival — i.e. everything else
  // is in place. Avoids noise on rejected / declared-empty parcels.
  const showNotInWarehouseHint =
    isInProgress &&
    parcel.status === 'APPROVED' &&
    products.length > 0 &&
    !isInWarehouse;

  return (
    <>
      <PageHeader
        title={parcel.name}
        subtitle={
          <div className="flex flex-wrap items-center gap-2">
            {!parcel.is_split && <ClientStatusChip status={clientStatus} />}
            {parcel.is_split && parcel.split_status && (
              <span className="inline-flex items-center gap-1.5">
                <span className="text-xs text-ink-muted">{t('parcels:splitLabel')}</span>
                <FeaturesSplitsStatusChip status={parcel.split_status} />
              </span>
            )}
          </div>
        }
        showBack
        backTo="/parcels"
      />
      <div className="flex flex-col gap-3 p-4 lg:gap-4 lg:p-6">
        <Section>
          <div className="flex items-center justify-between gap-2">
            <span className="text-sm font-bold text-ink">{t('parcels:trek', { trekId: parcel.trek_id })}</span>
            <span className="text-xs text-ink-muted">{formatDateTime(parcel.created_at)}</span>
          </div>
          {parcel.status === 'REJECTED' && parcel.reject_reason && (
            <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
              <strong className="font-semibold">{t('parcelDetail:rejectReason')}</strong>{' '}
              <span>{parcel.reject_reason}</span>
            </div>
          )}
        </Section>

        {!parcel.is_split && (
        <div className="grid grid-cols-1 gap-3 lg:grid-cols-2 lg:gap-4">
          <Section title={t('parcelDetail:sections.address')}>
            <Row label={t('parcelDetail:address.name')} value={parcel.address_name} />
            <Row label={t('parcelDetail:address.region')} value={t(`regions:${parcel.region}`)} />
            <Row label={t('parcelDetail:address.city')} value={parcel.city} />
            <Row
              label={t('parcelDetail:address.street')}
              value={`${parcel.street}, ${parcel.building}${parcel.apartment ? `/${parcel.apartment}` : ''}`}
            />
            {parcel.additional_info && (
              <Row label={t('parcelDetail:address.note')} value={parcel.additional_info} />
            )}
          </Section>

          <Section title={t('parcelDetail:sections.recipient')}>
            <Row
              label={t('parcelDetail:recipient.fullName')}
              value={`${parcel.last_name} ${parcel.first_name} ${parcel.middle_name}`}
            />
            <Row label={t('parcelDetail:recipient.phone')} value={parcel.phone_number} />
            <Row label={t('parcelDetail:recipient.passport')} value={parcel.passport_series} />
            <Row label={t('parcelDetail:recipient.pinfl')} value={parcel.pinfl} />
            {limitQuery.data && limitQuery.data.limit_applies !== false && (
              <Row
                label={t('parcelDetail:recipient.limit')}
                value={
                  <span
                    className={cn(
                      limitQuery.data.is_over_limit ||
                        limitQuery.data.used_amount > limitQuery.data.limit
                        ? 'text-danger'
                        : 'text-emerald-700',
                    )}
                  >
                    {formatUsd(limitQuery.data.used_amount)} / {formatUsd(limitQuery.data.limit)}
                  </span>
                }
              />
            )}
          </Section>
        </div>
        )}

        {parcel.is_split && <SplitRecipientsSection parcelId={parcel.id} />}

        {parcel.party && (
          <Section title={t('parcelDetail:sections.party')}>
            <div className="flex items-start justify-between gap-3 py-1.5 text-sm">
              <span className="text-ink-muted">{t('parcelDetail:partyName')}</span>
              <span className="text-right font-medium text-ink">{parcel.party.name}</span>
            </div>
            <div className="flex items-center justify-between gap-3 py-1.5 text-sm">
              <span className="text-ink-muted">{t('parcelDetail:partyStatusLabel')}</span>
              <PartyStatusChip status={parcel.party.status} />
            </div>
          </Section>
        )}

        <Section title={t('parcelDetail:sections.products', { count: products.length })}>
          {products.length === 0 ? (
            <p className="text-sm text-ink-muted">{t('parcelDetail:productsEmpty')}</p>
          ) : (
            <ul className="divide-y divide-slate-100">
              {products.map((p) => (
                <li key={p.id} className="flex items-center justify-between py-2 text-sm">
                  <span className="min-w-0 flex-1 truncate text-ink-soft">
                    {p.full_name} <span className="text-ink-muted">× {p.quantity}</span>
                  </span>
                  <span className="font-medium text-ink">
                    {formatUsd(p.quantity * p.price_per_item)}
                  </span>
                </li>
              ))}
            </ul>
          )}
          <div className="flex items-center justify-between border-t border-slate-100 pt-2 text-sm">
            <span className="text-ink-muted">{t('common:total')}</span>
            <span className="text-base font-semibold text-ink">{formatUsd(parcel.total_amount)}</span>
          </div>
        </Section>

        <ParcelChargesSection parcelId={parcel.id} />

        {showNotInWarehouseHint && (
          <p className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
            {t('parcels:confirmShipping.notInWarehouse')}
          </p>
        )}

        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 xl:grid-cols-4">
          {canConfirmShip && (
            <Button
              onClick={() => setConfirmShip(true)}
              loading={confirmShip$.isPending}
              leftIcon={<CheckCircle2Icon className="h-4 w-4" />}
              block
            >
              {t('parcels:confirmShipping.action')}
            </Button>
          )}
          {canDeclare && (
            <Button
              variant="outline"
              onClick={() => navigate(`/parcels/${parcel.id}/declaration`)}
              leftIcon={<FileTextIcon className="h-4 w-4" />}
              block
            >
              {t('parcelDetail:declaration')}
            </Button>
          )}
          {editable && (
            <Button
              variant="outline"
              onClick={() => navigate(`/parcels/create?edit=${parcel.id}`)}
              leftIcon={<Edit3Icon className="h-4 w-4" />}
              block
            >
              {t('parcelDetail:edit')}
            </Button>
          )}
          {deletable && (
            <Button
              variant="danger"
              onClick={() => setConfirmDelete(true)}
              leftIcon={<Trash2Icon className="h-4 w-4" />}
              block
            >
              {t('parcelDetail:delete')}
            </Button>
          )}
        </div>
      </div>

      <ConfirmDialog
        open={confirmShip}
        title={t('parcels:confirmShipping.confirmTitle')}
        description={t('parcels:confirmShipping.confirmDescription')}
        confirmLabel={t('parcels:confirmShipping.confirmAction')}
        loading={confirmShip$.isPending}
        onCancel={() => {
          if (!confirmShip$.isPending) setConfirmShip(false);
        }}
        onConfirm={() => confirmShip$.mutate(undefined)}
      />

      {deletable && (
        <ConfirmDialog
          open={confirmDelete}
          title={t('parcelDetail:confirmDeleteTitle')}
          description={
            parcel.status === 'APPROVED'
              ? t('parcelDetail:confirmDeleteApproved')
              : t('parcelDetail:confirmDeleteDefault')
          }
          confirmLabel={t('parcelDetail:confirmDeleteAction')}
          confirmVariant="danger"
          loading={remove.isPending}
          onCancel={() => setConfirmDelete(false)}
          onConfirm={() => remove.mutate(undefined)}
        />
      )}
    </>
  );
};
